import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import { useAuthStore } from '../stores/authStore';

interface Certificate {
  id: string;
  code: string;
  recipient_name: string;
  class_name: string;
  issued_at: string;
  is_revoked: boolean;
  class?: {
    id: string;
    title: string;
  };
  provider?: {
    id: string;
    company_name: string;
  };
}

export default function MyCertificatesPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCertificates();
  }, [user, navigate]);

  const fetchCertificates = async () => {
    try {
      const response = await fetch('/api/v1/certificates', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('인증서 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setCertificates(data.certificates || []);
    } catch (error: any) {
      console.error('Failed to fetch certificates:', error);
      alert(error.message || '인증서 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCertificate = (certId: string) => {
    // 인증서 화면은 워터마크 미리보기로 누구나 볼 수 있음.
    // 결제 게이트는 PDF 다운로드 시점에 적용됨.
    navigate(`/certificates/${certId}/view`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
          <AdminSidebar />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">로딩 중...</p>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />
        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div>
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">인증서 보관함</h1>
              <p className="text-gray-600">
                발급받은 인증서를 확인하고 관리할 수 있습니다.
              </p>
            </div>

            {/* Certificates Grid */}
            {certificates.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-2">발급받은 인증서가 없습니다.</p>
                <p className="text-gray-500 text-sm">
                  수업을 완료하고 승인받으면 인증서가 발급됩니다.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {certificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
                  >
                    {/* Certificate Header */}
                    <div className="bg-gradient-to-r from-primary to-primary/80 p-4">
                      <div className="flex items-center gap-2 text-white">
                        <Award className="w-5 h-5" />
                        <span className="text-sm font-semibold">인증서</span>
                      </div>
                    </div>

                    {/* Certificate Body */}
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 mb-2 text-lg line-clamp-2">
                        {cert.class_name}
                      </h3>
                      <div className="space-y-2 text-sm text-gray-600 mb-4">
                        <div className="flex justify-between">
                          <span>발급 업체:</span>
                          <span className="font-medium text-gray-900">
                            {cert.provider?.company_name || '알 수 없음'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>발급일:</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(cert.issued_at)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>인증번호:</span>
                          <span className="font-mono text-xs text-gray-700">
                            {cert.code}
                          </span>
                        </div>
                      </div>

                      {/* View Button */}
                      <button
                        onClick={() => handleViewCertificate(cert.id)}
                        className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        인증서 보기
                      </button>

                      {/* Revoked Badge */}
                      {cert.is_revoked && (
                        <div className="mt-2 text-center">
                          <span className="inline-block px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded">
                            취소됨
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
